import React from 'react'
import { ValidationError, api } from '../api/client'
import type { ClientDetail, Consent } from '../types/app'
import { consentState } from './consent'
import { CONSENT_WORDING } from './wording'
import { formatDay } from '../catalog/format'
import { saveErrorMessage } from '../screens/settings/toast'

// consentSource says where a grant came from: the form whose signature
// recorded it, or the professional recording it in person.
function consentSource(c: Consent): string {
  if (c.source === 'professional') return 'recorded by you'
  return c.form_name ?? 'consultation form'
}

// asDay turns a full timestamp into the date-only string formatDay expects.
function asDay(isoDateTime: string): string {
  return formatDay(isoDateTime.slice(0, 10))
}

function validationMessage(error: ValidationError): string {
  return Object.values(error.fields).join(' ') || 'Check what you entered and try again.'
}

// What differs between the two kinds; the row itself behaves the same.
const COPY = {
  records: {
    grant: 'Record consent given in person',
    withdrawConfirm: (first: string) =>
      `Record that ${first} has withdrawn consent? Their notes stay until you erase them. Signing a consultation form again records new consent.`,
    withdrawButton: 'Withdraw consent',
  },
  photos: {
    grant: 'Record agreement',
    withdrawConfirm: (first: string) =>
      `Record that ${first} has withdrawn agreement to photos? Photos already taken stay with their notes until you remove them.`,
    withdrawButton: 'Withdraw agreement',
  },
} as const

interface Props {
  kind: 'records' | 'photos'
  label: string
  client: ClientDetail
  businessName: string
  // The new consent row the API returned, newest first in the history.
  onRecorded: (consent: Consent) => void
  // What the status line says when nothing is recorded yet.
  emptyHint: string
}

// ConsentRow is one kind of consent on the profile: where it stands, and the
// way to record it in person or record a withdrawal, each as a new row.
export default function ConsentRow({ kind, label, client, businessName, onRecorded, emptyHint }: Props) {
  const [action, setAction] = React.useState<'grant' | 'withdraw' | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  const openAction = (next: 'grant' | 'withdraw' | null) => {
    setError('')
    setAction(next)
  }

  const confirm = async (granted: boolean) => {
    setSaving(true)
    setError('')
    try {
      const consent = await api.recordConsent(client.id, {
        kind,
        granted,
        wording_version: CONSENT_WORDING[kind].version,
      })
      onRecorded(consent)
      setAction(null)
    } catch (e) {
      setError(e instanceof ValidationError ? validationMessage(e) : saveErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  const copy = COPY[kind]
  const status = consentState(client.consent, kind)
  const latest = client.consent.find((c) => c.kind === kind)
  const lastGrant = client.consent.find((c) => c.kind === kind && c.granted)
  return (
    <div className="profile-consent-row">
      <div className="profile-consent-row__head">
        <div className="profile-consent-row__main">
          <span className="profile-consent-row__label">{label}</span>
          {status === 'granted' && latest && (
            <span className="profile-consent-row__status">
              Granted on {asDay(latest.granted_at)} · {consentSource(latest)}.
            </span>
          )}
          {status === 'withdrawn' && latest?.withdrawn_at && (
            <span className="profile-consent-row__status">
              Withdrawn on {asDay(latest.withdrawn_at)}.
              {lastGrant && ` Granted on ${asDay(lastGrant.granted_at)} · ${consentSource(lastGrant)}.`}
            </span>
          )}
          {status === 'not_recorded' && <span className="profile-consent-row__status">{emptyHint}</span>}
        </div>
        {action === null &&
          (status === 'granted' ? (
            <button type="button" className="btn btn-quiet btn-compact" onClick={() => openAction('withdraw')}>
              Withdraw
            </button>
          ) : (
            <button type="button" className="btn btn-quiet btn-compact" onClick={() => openAction('grant')}>
              {copy.grant}
            </button>
          ))}
      </div>
      {action !== null && (
        <div className="profile-consent-confirm">
          {action === 'grant' ? (
            <p>{CONSENT_WORDING[kind].text.replace('[business]', businessName)}</p>
          ) : (
            <p>{copy.withdrawConfirm(client.first_name)}</p>
          )}
          {error && <p className="field-error" role="alert">{error}</p>}
          <div className="profile-consent-confirm__actions">
            <button type="button" className="btn btn-secondary btn-compact" onClick={() => openAction(null)} disabled={saving}>
              Cancel
            </button>
            {action === 'grant' ? (
              <button type="button" className="btn btn-primary" onClick={() => confirm(true)} disabled={saving}>
                {saving ? 'Recording…' : 'Confirm'}
              </button>
            ) : (
              <button type="button" className="btn btn-danger btn-compact" onClick={() => confirm(false)} disabled={saving}>
                {saving ? 'Withdrawing…' : copy.withdrawButton}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
